import * as S3StorageService from 'common/services/S3StorageService';
import { Document } from 'common/types/documentRequirements';
import { upload } from 'common/services/FileService';
import { DOCUMENT_TYPE } from 'common/stores/redux/MetaStore/MetaStoreApi.types';
import Alert from '../Alert';

export const getVideoInfoFromUploadFile = async (videoFile: File) => {
    if (!videoFile) {
        throw new Error('Invalid Video File');
    }
    const objectUrl = URL.createObjectURL(videoFile);
    return getVideoInfoFromURL(objectUrl);
};

export const getVideoInfoFromURL = (
    url: string
): Promise<{ thumbnailUrl: string; duration: number }> => {
    return new Promise((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.crossOrigin = 'anonymous';
        video.muted = true;
        video.playsInline = true;
        video.src = url;

        video.onloadedmetadata = () => {
            video.currentTime = 0.001;
        };

        video.onseeked = () => {
            try {
                if (video.videoWidth === 0 || video.videoHeight === 0) {
                    reject(new Error('Video dimensions not available'));
                    return;
                }

                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnailUrl = canvas.toDataURL('image/jpeg');
                resolve({ thumbnailUrl, duration: video.duration });
            } catch (error) {
                reject(error);
            } finally {
                if (url) {
                    URL.revokeObjectURL(url);
                }
            }
        };

        video.onerror = () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
            reject(new Error('Error loading video for thumbnail generation'));
        };
    });
};

export const convertSecondsToMinuteSecond = (totalSeconds: number) => {
    const minutes = Math.floor(totalSeconds / 60); // Get the number of full minutes.
    const seconds = Math.floor(totalSeconds % 60); // Get the remainder seconds.

    // Use padStart(2, '0') to ensure two digits for both minutes and seconds.
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');

    return `${formattedMinutes}:${formattedSeconds}`;
};

export const isVideoMimeType = (mimeType: string): boolean => {
    return mimeType?.startsWith('video/');
};

export const uploadPhoto = async ({
    file,
    document,
    typeId,
    description,
    requiresPullAndClean,
    requirementTypeId = 'PHOTO',
}: {
    file: File;
    document: Document;
    typeId: string;
    description: string;
    requiresPullAndClean: boolean;
    requirementTypeId: string;
}): Promise<{ result: any; requirement: any } | undefined> => {
    if (file.size > 10000000) {
        Alert.warning(
            `File size ${(file.size / 1000000).toPrecision(
                3
            )}MB too large, maximum 10MB. File was not uploaded.`
        );
        return;
    }
    const result = await upload(file);

    const requirement =
        document.documentTypeId === DOCUMENT_TYPE.QUOTE
            ? getQuotePhotoRequirements(
                  result,
                  file,
                  typeId,
                  description,
                  requiresPullAndClean,
                  requirementTypeId
              )
            : getWorkOrderPhotoRequirement(
                  result,
                  file,
                  typeId,
                  description,
                  requiresPullAndClean,
                  requirementTypeId
              );
    return { result, requirement };
};

export const getVideoRequirement = async ({
    file,
    documentId,
    documentTypeId,
    typeId,
    description,
    requiresPullAndClean,
    requirementTypeId = 'PHOTO',
}: {
    file: File;
    documentId: number;
    documentTypeId: string;
    typeId: string;
    description: string;
    requiresPullAndClean: boolean;
    requirementTypeId: string;
}) => {
    const { duration } = await getVideoInfoFromUploadFile(file);
    if (duration > 61) {
        Alert.warning('Video cannot exceed 1 minute.');
        return;
    }

    const result = await S3StorageService.getRequirementUploadMetaData({
        fileName: file.name,
        resourceType: file.type,
        documentId,
        modelName:
            documentTypeId === DOCUMENT_TYPE.QUOTE ? 'Quote' : 'WorkOrder',
        photoType: typeId,
        requirementType: requirementTypeId,
    });
    if (result.isError && result.errorMessage && !result.error) {
        Alert.warning(result.errorMessage);
        return;
    }
    if (result.isError) {
        Alert.error('Unable to upload file: Invalid image file');
        return;
    }
    const url = `https://${result.requirement.video.s3Bucket}/${result.requirement.video.s3Key}`;
    const mimeType = 'video/mp4';
    const requirement =
        documentTypeId === DOCUMENT_TYPE.QUOTE
            ? getQuoteVideoRequirements(
                  result.requirement,
                  file,
                  typeId,
                  description,
                  requiresPullAndClean,
                  url,
                  mimeType,
                  requirementTypeId
              )
            : getWorkOrderVideoRequirement(
                  result.requirement,
                  file,
                  typeId,
                  description,
                  requiresPullAndClean,
                  url,
                  mimeType,
                  requirementTypeId
              );
    return { result, requirement };
};

export interface ManipulateRequirement {
    file: File;
    document: Document;
    typeId: string;
    description: string;
    requiresPullAndClean: boolean;
    requirementTypeId?: string;
}

export const manipulateRequirement = async ({
    file,
    document,
    typeId,
    description,
    requiresPullAndClean,
    requirementTypeId = 'PHOTO',
}: ManipulateRequirement) => {
    if (isVideoMimeType(file.type)) {
        return getVideoRequirement({
            file,
            documentId: document.id,
            documentTypeId: document.documentTypeId,
            typeId,
            description,
            requiresPullAndClean,
            requirementTypeId,
        });
    }
    return uploadPhoto({
        file,
        document,
        typeId,
        description,
        requiresPullAndClean,
        requirementTypeId,
    });
};

const getWorkOrderPhotoRequirement = (
    result: any,
    file: File,
    typeId: string,
    description: string,
    requiresPullAndClean: boolean,
    requirementTypeId: string = 'PHOTO',
    url: string = result.secure_url,
    mimeType: string = file.type
) => {
    const baseRequirement = getBaseRequirement(
        file,
        typeId,
        description,
        requiresPullAndClean,
        url,
        mimeType
    );

    return {
        requirement: {
            ...baseRequirement,
            photo: result,
        },
        requirementTypeId,
    };
};
const getWorkOrderVideoRequirement = (
    result: any,
    file: File,
    typeId: string,
    description: string,
    requiresPullAndClean: boolean,
    url: string = result.secure_url,
    mimeType: string = file.type,
    requirementTypeId: string = 'PHOTO'
) => {
    const baseRequirement = getBaseRequirement(
        file,
        typeId,
        description,
        requiresPullAndClean,
        url,
        mimeType
    );
    return {
        requirement: {
            ...baseRequirement,
            ...result,
        },
        requirementTypeId,
    };
};

const getQuotePhotoRequirements = (
    result: any,
    file: File,
    typeId: string,
    description: string,
    requiresPullAndClean: boolean,
    requirementTypeId: string = 'PHOTO',
    url: string = result.secure_url,
    mimeType: string = file.type
) => {
    const baseRequirement = getBaseRequirement(
        file,
        typeId,
        description,
        requiresPullAndClean,
        url,
        mimeType
    );
    return {
        ...baseRequirement,
        photo: result,
        created: result.created,
        requirementTypeId,
    };
};

const getQuoteVideoRequirements = (
    result: any,
    file: File,
    typeId: string,
    description: string,
    requiresPullAndClean: boolean,
    url: string = result.secure_url,
    mimeType: string = file.type,
    requirementTypeId: string = 'PHOTO'
) => {
    const baseRequirement = getBaseRequirement(
        file,
        typeId,
        description,
        requiresPullAndClean,
        url,
        mimeType
    );
    return {
        ...baseRequirement,
        created: result.created,
        requirementTypeId,
        ...result,
    };
};

const getBaseRequirement = (
    file: File,
    typeId: string,
    description: string,
    requiresPullAndClean: boolean,
    url: string,
    mimeType: string
) => ({
    url,
    fileName: file.name,
    mimeType,
    photoTypeId: typeId,
    description,
    requiresPullAndClean,
});
